/*
 * VivaLynx — site JS.
 *
 * Vanilla only, no jQuery. Motion is kept to a minimum on accessibility
 * grounds, so this file stays small on purpose: anything that is three lines
 * of CSS belongs in CSS.
 */

import '../css/main.css';

/**
 * Mobile navigation toggle.
 *
 * The nav renders open-capable in markup and is hidden by CSS at narrow
 * widths, so it still works with JS unavailable.
 */
function initNav() {
  const toggle = document.querySelector('.nav__toggle');
  const list = document.querySelector('.nav__list');

  if (!toggle || !list) return;

  // Tells the stylesheet the disclosure is live. Without it the links stay
  // visible, because a button that cannot open is worse than a long nav.
  toggle.closest('.hdr')?.setAttribute('data-nav', 'ready');

  toggle.addEventListener('click', () => {
    const open = list.dataset.open === 'true';
    list.dataset.open = String(!open);
    toggle.setAttribute('aria-expanded', String(!open));
  });

  // Escape closes it and returns focus to the control that opened it.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || list.dataset.open !== 'true') return;
    list.dataset.open = 'false';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.focus();
  });
}

/**
 * Reveal-on-scroll.
 *
 * IntersectionObserver rather than a scroll listener. Honours
 * prefers-reduced-motion by revealing everything immediately, which is why
 * the initial hidden state is set here rather than in CSS — with JS or
 * motion unavailable, content must never be stuck invisible.
 */
function initReveals() {
  const targets = document.querySelectorAll('[data-reveal]');

  if (!targets.length) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduced || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.setAttribute('data-revealed', 'true'));
    return;
  }

  targets.forEach((el) => el.setAttribute('data-revealed', 'false'));

  // Whatever is already on screen when the page opens settles in on a
  // stagger rather than all at once, which is the difference between a page
  // that opens and a page that appears. Everything below the fold waits for
  // the observer.
  const fold = window.innerHeight;
  let opened = 0;

  targets.forEach((el) => {
    if (el.getBoundingClientRect().top > fold) return;
    const delay = opened * 90;
    opened += 1;
    window.setTimeout(() => el.setAttribute('data-revealed', 'true'), delay);
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-revealed', 'true');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  targets.forEach((el) => observer.observe(el));
}


/**
 * The care circle plays when it is on screen, and stops when it is not.
 *
 * It autoplays, which the brand guide's motion rules otherwise disallow —
 * a deliberate exception for marketing, decided 2026-09-25. Narrowed as far
 * as it can be: nothing runs off screen, and under prefers-reduced-motion
 * the class is never applied, so the diagram holds the resting frame that
 * carries the argument on its own.
 *
 * The animation is paused in CSS rather than unset, so with JS unavailable
 * the circle still renders every label and reads correctly; it simply does
 * not move.
 */
function initCareCircle() {
  // Both takes: .circle is the ring version kept in docs/care-circle,
  // .lynx is the one drawn on the mark. Whichever is on the page gets it.
  const circles = document.querySelectorAll('.circle, .lynx, .eco, .vmap, .c4');

  if (!circles.length) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduced) return;

  // Past the reduced-motion check, so this marker is never set when motion
  // is reduced. The ecosystem drawing BUILDS its content in keyframes, so
  // without the marker its rows stay visible and it rests on the finished
  // record — the same rule the reveal system follows: the hidden state is
  // set by the script, never in CSS, or a page without JavaScript is stuck
  // showing an empty panel.
  circles.forEach((el) => { el.dataset.anim = 'on'; });

  if (!('IntersectionObserver' in window)) {
    circles.forEach((el) => el.classList.add('is-inview'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-inview', entry.isIntersecting);
      });
    },
    { threshold: 0.25 }
  );

  circles.forEach((el) => observer.observe(el));
}

/**
 * Stop each travelling word ON the boundary line.
 *
 * The build on How it works sends five words across a dashed boundary, and
 * each has to come to rest with its right edge exactly on the line. The
 * words are different widths, so one shared distance cannot do it and the
 * value is not knowable until the text has been laid out — which is why it
 * is measured here and written to --tx rather than baked into the CSS.
 *
 * Runs again after document.fonts.ready: measured against the fallback face
 * the widths are wrong, and the short words end up stopping short, hanging
 * in open space. The first call is not redundant — if the font promise never
 * settles the words still land somewhere sensible.
 *
 * Does nothing where the component is absent, which is every page but one.
 */
/**
 * The care map leans toward the pointer.
 *
 * An ambient animation on this diagram failed twice: a breath subtle enough
 * not to pull the eye off the copy beside it measured 1.4px and could not be
 * seen, and running the same breath on :hover only reported that something
 * was animating. Tilting toward the cursor is a different kind of thing —
 * the motion is tied to the reader's own hand, so it reads as the diagram
 * answering rather than as decoration playing.
 *
 * Pointer position cannot come from CSS, so this is the one drawing on the
 * site with a JavaScript motion behaviour. Everything degrades: no script,
 * no pointer, or reduced motion, and it is simply a still diagram, which is
 * what it was built to be.
 *
 * Per-frame work is one transform write, coalesced through rAF so a fast
 * pointer cannot queue more updates than the display can show.
 */
function initPointerTilt() {
  const maps = document.querySelectorAll('.vmap');

  if (!maps.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // A coarse or absent pointer gets the CSS breath instead; tracking a
  // finger that is covering the diagram is not worth having.
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  // Degrees at the very corner. Past about 9 the labels start to look
  // sheared rather than tilted, because the type is rendered flat.
  const MAX_TILT = 7;
  const LIFT = 1.02;

  maps.forEach((map) => {
    let frame = 0;
    let pending = null;

    const apply = () => {
      frame = 0;

      if (!pending) return;

      const box = map.getBoundingClientRect();

      if (!box.width || !box.height) return;

      // −0.5 … 0.5 from the centre, so the sign gives the direction.
      const x = (pending.clientX - box.left) / box.width - 0.5;
      const y = (pending.clientY - box.top) / box.height - 0.5;

      // rotateX is negated: pointer below centre should tip the far edge
      // away, which is a negative rotation about X.
      map.style.transform =
        `perspective(1200px) rotateX(${(-y * MAX_TILT * 2).toFixed(2)}deg) ` +
        `rotateY(${(x * MAX_TILT * 2).toFixed(2)}deg) scale(${LIFT})`;
    };

    map.addEventListener('pointermove', (event) => {
      pending = event;
      map.classList.add('vmap--tilting');

      if (!frame) frame = requestAnimationFrame(apply);
    });

    const release = () => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }

      pending = null;
      // Dropping the class restores the longer easing, so letting go springs
      // back rather than snapping.
      map.classList.remove('vmap--tilting');
      map.style.transform = '';
    };

    map.addEventListener('pointerleave', release);
    map.addEventListener('pointercancel', release);
  });
}

function initCarryAim() {
  const builds = document.querySelectorAll('.c4');

  if (!builds.length) return;

  const aim = () => {
    builds.forEach((build) => {
      const bound = build.querySelector('.c4__bound');

      if (!bound) return;

      const line = parseFloat(bound.getAttribute('x1'));

      build.querySelectorAll('.c4__carry').forEach((word) => {
        // getBBox throws on a node that is not being rendered — a display:none
        // ancestor, or a tab that has never been shown.
        let box;

        try {
          box = word.getBBox();
        } catch (e) {
          return;
        }

        if (!box.width) return;

        word.style.setProperty('--tx', `${(line - (box.x + box.width)).toFixed(1)}px`);
      });
    });
  };

  aim();

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(aim);
  }
}

/**
 * Deep links into a CSS accordion.
 *
 * The accordions on How it works and Providers are radio-driven and have no
 * JavaScript of their own, which is why they work with none. A fragment
 * does not check a radio, though, so an inbound #d3 would scroll to the
 * accordion and leave item one open. This checks the one that was asked
 * for. Without JavaScript the link still lands in the right place, on the
 * default item, which is a worse answer rather than a broken one.
 */
function initAccordionLinks() {
  const open = () => {
    const id = window.location.hash.slice(1);

    if (!/^[a-z]\d{1,2}$/.test(id)) return;

    const input = document.getElementById(id);

    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (input.checked) return;

    input.checked = true;
  };

  open();
  window.addEventListener('hashchange', open);
}

/**
 * The day-in-the-life accordion, advancing on its own.
 *
 * The accordion itself stays CSS-only and radio-driven: with JavaScript off
 * it opens on item one and every label still works. This adds the moving
 * part on top and takes it away the moment anybody touches it.
 *
 * The dwell is a CSS animation on a bar, and the advance is that animation
 * finishing. Not a setTimeout beside it. Two clocks for one interval drift
 * apart, and the interval already has to exist in CSS to draw the bar --
 * this way the script never reads a CSS time at all, which is the bug that
 * cost an afternoon on the pilot rail.
 *
 * It waits on the animation's own finished promise rather than listening for
 * animationend. Same moment, but the promise is a property of the animation
 * and the event is dispatched by the frame loop, so the promise is the one
 * that can be driven and checked. Pausing goes through the same object,
 * which also means one place holds the play state instead of CSS and script
 * each holding half of it.
 *
 * One bar element, moved to whichever item is open, rather than one per
 * item: the animation starts because the element is inserted, so there is
 * nothing to reset and one rule in the stylesheet instead of eight.
 */
function initDayAuto() {
  document.querySelectorAll('.day[data-auto]').forEach(armDayAuto);
}

/**
 * One self-advancing accordion.
 *
 * Opt-in by attribute rather than by id, because two pages run this now --
 * How it works and providers -- and they use different radio names. The
 * name is read off the first radio in the group instead of being written
 * here, so a third one needs markup and nothing else.
 *
 * @param {Element} day The .day grid holding the radios, the list and the screens.
 */
function armDayAuto(day) {
  const acc = day.querySelector('.acc');
  const first = day.querySelector('input[type="radio"]');

  if (!acc || !first) return;

  const inputs = [...day.querySelectorAll(`input[type="radio"][name="${first.name}"]`)];

  if (inputs.length < 2) return;

  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
  let bar = null;
  let run = null;
  let stopped = calm.matches;
  let held = false;

  const clear = () => {
    if (bar) bar.remove();
    bar = null;
    run = null;
  };

  const step = () => {
    const at = inputs.findIndex((input) => input.checked);

    inputs[(at + 1) % inputs.length].checked = true;
    arm();
  };

  function arm() {
    clear();

    if (stopped) return;

    const open = inputs.find((input) => input.checked) || inputs[0];
    const label = acc.querySelector(`label[for="${open.id}"]`);

    if (!label) return;

    bar = document.createElement('span');
    bar.className = 'acc__bar';
    bar.setAttribute('aria-hidden', 'true');
    label.appendChild(bar);

    const mine = bar;

    run = mine.getAnimations()[0] || null;

    // No animation means no clock, and a clock of our own is the thing this
    // is avoiding. It just stays where it is.
    if (!run) return;

    if (held) run.pause();

    // Rejects when the bar is removed under it, which is every advance and
    // every stop. Nothing to report.
    run.finished.then(() => {
      if (bar === mine) step();
    }).catch(() => {});
  }

  const hold = (on) => {
    held = on;

    if (!run) return;

    if (on) run.pause();
    else run.play();
  };

  // A button, not just "click a panel and it stops". 2.2.2 wants a mechanism
  // to pause content that moves on its own, and clicking a panel is not one:
  // it is unlabelled, and it changes what you are reading to use it.
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'acc__pause';
  const label = (on) => {
    button.setAttribute('aria-label', on ? 'Play the walkthrough' : 'Pause the walkthrough');
    button.dataset.state = on ? 'paused' : 'playing';
  };

  const stop = () => {
    stopped = true;
    clear();
    button.remove();
  };

  button.addEventListener('click', () => {
    hold(!held);
    label(held);
  });

  // Any real interaction hands it over for good. Setting .checked from here
  // does not fire change, so this only ever sees the visitor.
  day.addEventListener('change', stop);

  // Nothing advances off screen or in a background tab -- panels going by
  // unseen is the same as not having them.
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => hold(!entry.isIntersecting));
    }, { threshold: 0.2 }).observe(day);
  }

  document.addEventListener('visibilitychange', () => hold(document.hidden));

  if (stopped) return;

  label(false);

  // Inside .acc, not before it. .day is a two-column grid holding exactly
  // the list and the phone; a third child there takes a cell of its own and
  // pushes the screen out of the row. .acc is a flex column and has room.
  acc.prepend(button);
  arm();
}

/**
 * After a submission the page comes back with the form replaced by its
 * confirmation, and a fragment pointing at it.
 *
 * The browser's own fragment jump cannot be relied on here. It resolves
 * before initReveals() has run, and that sets every [data-reveal] section to
 * opacity 0 and 12px down -- so the jump measures a position that is about
 * to change, and lands at the top of the page instead. Observed on staging:
 * the same URL scrolled correctly once and not at all the next time.
 *
 * So do it explicitly, and in this order: reveal the section first (a
 * confirmation should not fade in below the fold), then scroll, then focus.
 * Without the focus move a screen reader announces nothing -- role="status"
 * only fires on a live change, not on content that was there at load -- and
 * the next Tab goes wherever the form used to be.
 *
 * behavior:'instant' rather than inheriting html { scroll-behavior:smooth }.
 * The families page puts its form about 4,000px down, and an animated scroll
 * that far was still at the top of the page a second and a half later --
 * reveals firing during the animation appear to cancel it. Instant is also
 * simply the right answer for a jump that happens on page load: there is no
 * orientation to preserve, the previous view is gone either way.
 */
function initFormSent() {
  const done = document.querySelector('.formdone');

  if (!(done instanceof HTMLElement)) return;

  const settle = () => {
    const section = done.closest('[data-reveal]');

    if (section) section.setAttribute('data-revealed', 'true');

    (done.closest('.formwrap') || done).scrollIntoView({ block: 'start', behavior: 'instant' });
    done.focus({ preventScroll: true });
  };

  if (document.readyState === 'complete') {
    settle();
  } else {
    window.addEventListener('load', settle, { once: true });
  }
}

/**
 * The pilot sequence on the payers page.
 *
 * Keyed off the class rather than an id, so more than one can exist, and
 * initialised with everything else rather than from an inline script.
 *
 * The rail counts STEPS. The intro is a title card and is not one, which is
 * why the rail sits empty under it. No pause control in this version -- see
 * parts/pilot-steps.php.
 */
function initPilotSteps() {
  document.querySelectorAll('.vlx-steps').forEach((root) => {
    const panels = [...root.querySelectorAll('.vlx-step')];
    const rail = root.querySelector('.vlx-rail');

    if (!panels.length || !rail) return;

    // parseInt is not safe on a CSS time. The prototype used it because its
    // CSS was never minified, so the value was the literal '2200ms'. Through
    // the build it comes back as '2.2s', and parseInt('2.2s') is 2 -- which
    // once had this advancing every two milliseconds and looking frozen.
    const readMs = (value, fallback) => {
      const raw = String(value).trim();
      const n = parseFloat(raw);

      if (!Number.isFinite(n)) return fallback;

      return /ms$/i.test(raw) ? n : n * 1000;
    };

    const base = readMs(getComputedStyle(root).getPropertyValue('--step-dwell'), 2200);
    const steps = panels.filter((p) => !p.hasAttribute('data-intro'));

    steps.forEach(() => {
      const seg = document.createElement('span');
      seg.className = 'vlx-seg';
      seg.appendChild(document.createElement('i'));
      rail.appendChild(seg);
    });

    const segs = [...rail.children];

    let i = 0;
    let timer = null;

    // data-dwell is a bare number of milliseconds, so parseInt is right here.
    const dwellFor = (n) => parseInt(panels[n].dataset.dwell, 10) || base;

    // How many real steps precede this panel; -1 while the intro shows.
    const segIndex = (n) =>
      panels[n].hasAttribute('data-intro')
        ? -1
        : panels.slice(0, n).filter((p) => !p.hasAttribute('data-intro')).length;

    function paint() {
      panels.forEach((el, n) => {
        el.dataset.state = n === i ? 'in' : 'out';
      });

      const active = segIndex(i);

      segs.forEach((el, n) => {
        el.removeAttribute('data-seg');
        if (active > -1 && n < active) el.dataset.seg = 'done';
        if (n !== active) return;

        // Restart the fill on each advance, or it keeps the old run.
        const fill = el.firstChild;
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
        fill.style.animationDuration = `${dwellFor(i)}ms`;
        el.dataset.seg = 'active';
      });
    }

    function stop() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function schedule() {
      stop();
      timer = setTimeout(() => {
        i = (i + 1) % panels.length;
        paint();
        schedule();
      }, dwellFor(i));
    }

    // Nothing moves in a background tab.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else schedule();
    });

    paint();
    schedule();
  });
}

/**
 * The console demo on the providers page.
 *
 * autoplay is in the MARKUP, so the clip runs with JavaScript off. This only
 * ever takes it away: under prefers-reduced-motion the video holds on its
 * poster until someone presses play, and an observer pauses it off screen.
 * preload is "none" in the markup and raised to "metadata" here, so a
 * visitor who never reaches this section never fetches the file.
 *
 * The native controls attribute is deliberately absent -- it puts a scrubber
 * over the picture on hover. This button is the pause mechanism WCAG 2.2.2
 * asks for. data-paused on the video drives the icon and keeps the button
 * visible while paused, so it is never a control you cannot find again.
 */
function initDemoVideo() {
  document.querySelectorAll('.demo__v').forEach((video) => {
    const button = video.parentElement.querySelector('.demo__pause');
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

    // The flag follows the element, however it was paused -- button, key,
    // observer or the browser declining to autoplay at all.
    const mark = () => {
      video.dataset.paused = String(video.paused);
      if (button) {
        button.setAttribute('aria-label', video.paused ? 'Play the demo' : 'Pause the demo');
      }
    };

    video.addEventListener('play', mark);
    video.addEventListener('pause', mark);

    // Set by hand rather than by the observer, so it survives the visitor
    // scrolling past a paused video and back.
    let wanted = !calm.matches;

    if (button) {
      button.addEventListener('click', () => {
        wanted = video.paused;
        if (wanted) video.play().catch(() => {});
        else video.pause();
      });
    }

    if (calm.matches) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
    } else {
      video.preload = 'metadata';
    }

    mark();

    if (!('IntersectionObserver' in window)) return;

    // Nothing plays off screen, and nothing resumes that the visitor paused.
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && wanted) video.play().catch(() => {});
          else if (!entry.isIntersecting) video.pause();
        });
      },
      { threshold: 0.25 }
    ).observe(video);
  });
}

function init() {
  initNav();
  initReveals();
  initCareCircle();
  initCarryAim();
  initPointerTilt();
  initAccordionLinks();
  initDayAuto();
  initFormSent();
  initPilotSteps();
  initDemoVideo();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
